/**
 * Stale generatora umow/aneksow (Faza 4a).
 *
 * Szablon = Tiptap HTML z {{zmiennymi}}. Zmienne z registry ponizej sa
 * auto-podstawiane z danych (lead PII / oferta / projekt / system).
 * Zmienne SPOZA registry ("manual") user uzupelnia w formularzu przy
 * generacji — to feature, nie blad (np. {{numer_aneksu}}).
 */

import type { ProjectDocType } from '../types/project.js';

/**
 * Wbudowane typy szablonow — SUGESTIE, nie zamkniety enum. Admin moze
 * wpisac wlasny typ (Dawid 2026-06-13: "klient musi miec mozliwosc
 * okreslenia swojego typu"). Custom typ = dowolny string 2-40 znakow,
 * wyswietlany 1:1; w project_files dostaje docType 'inne'.
 */
export const DOCUMENT_TEMPLATE_TYPES = ['umowa', 'aneks', 'protokol', 'inny'] as const;
export type DocumentTemplateTypeBuiltin = (typeof DOCUMENT_TEMPLATE_TYPES)[number];
// biome-ignore lint/complexity/noBannedTypes: `string & {}` — trick zachowujacy autocomplete built-in przy unii ze stringiem
export type DocumentTemplateType = DocumentTemplateTypeBuiltin | (string & {});

export const DOCUMENT_TEMPLATE_TYPE_LABELS: Record<DocumentTemplateTypeBuiltin, string> = {
  umowa: 'Umowa',
  aneks: 'Aneks',
  protokol: 'Protokół',
  inny: 'Inny dokument',
};

/** Etykieta typu: built-in ma polska nazwe, custom wyswietlamy 1:1. */
export function templateTypeLabel(type: string): string {
  return (DOCUMENT_TEMPLATE_TYPE_LABELS as Record<string, string>)[type] ?? type;
}

/**
 * Typ szablonu → docType w project_files (gdzie laduje wygenerowany PDF).
 * umowa/aneks maja dedykowane typy w enum; wszystko inne (protokol/inny/
 * custom) → 'inne'.
 */
const TEMPLATE_TYPE_TO_DOC_TYPE: Record<string, ProjectDocType> = {
  umowa: 'umowa',
  aneks: 'aneks',
};

export function templateTypeToDocType(type: string): ProjectDocType {
  return TEMPLATE_TYPE_TO_DOC_TYPE[type] ?? 'inne';
}

export type TemplateVariableSource = 'lead' | 'offer' | 'project' | 'system' | 'input';

export interface TemplateVariableDef {
  /** Slug w {{...}} — lowercase + underscore */
  key: string;
  /** Etykieta PL w edytorze i formularzu generacji */
  label: string;
  /** Skad backend bierze wartosc */
  source: TemplateVariableSource;
  /** Krotki opis dla admina w panelu zmiennych */
  description: string;
}

/**
 * Registry zmiennych auto-podstawianych. Resolver:
 * `apps/api/src/services/templateVariables.ts`.
 * Kazda nowa zmienna = wpis tutaj + case w resolverze (typecheck pilnuje
 * przez TEMPLATE_VARIABLE_KEYS w testach resolvera).
 */
export const TEMPLATE_VARIABLES: TemplateVariableDef[] = [
  // Lead (PII — deszyfrowane server-side przy generacji)
  { key: 'klient_imie_nazwisko', label: 'Imię i nazwisko klienta', source: 'lead', description: 'Z leada projektu' },
  { key: 'klient_adres', label: 'Adres klienta', source: 'lead', description: 'Adres zamieszkania (nie budowy)' },
  { key: 'klient_telefon', label: 'Telefon klienta', source: 'lead', description: 'Z leada projektu' },
  { key: 'klient_email', label: 'E-mail klienta', source: 'lead', description: 'Z leada projektu' },
  { key: 'klient_miasto', label: 'Miasto klienta', source: 'lead', description: 'Z leada projektu' },

  // Oferta (origin_offer_id projektu)
  { key: 'oferta_numer', label: 'Numer oferty', source: 'offer', description: 'Format N / MM / RRRR' },
  { key: 'oferta_data', label: 'Data oferty', source: 'offer', description: 'Data wystawienia oferty, DD.MM.RRRR (umowy powołują się na nią jako Załącznik)' },
  { key: 'kwota_netto', label: 'Kwota netto', source: 'offer', description: 'Suma netto z oferty PO rabacie, format PLN' },
  { key: 'kwota_brutto', label: 'Kwota brutto', source: 'offer', description: 'Suma brutto z oferty PO rabacie (= „Do zapłaty” z PDF), format PLN' },
  { key: 'kwota_slownie', label: 'Kwota brutto słownie', source: 'offer', description: 'Suma brutto PO rabacie zapisana słownie (na umowę)' },
  { key: 'kwota_netto_slownie', label: 'Kwota netto słownie', source: 'offer', description: 'Suma netto PO rabacie słownie — umowy o roboty budowlane operują na NETTO' },
  { key: 'kwota_vat', label: 'Kwota VAT', source: 'offer', description: 'Suma VAT po rabacie (kwota brutto − netto)' },
  { key: 'rabat_procent', label: 'Rabat (%)', source: 'offer', description: 'Procent rabatu z oferty, 0 gdy brak' },
  { key: 'zakres_prac', label: 'Zakres prac', source: 'offer', description: 'Lista pozycji oferty pogrupowana etapami' },

  // Projekt
  { key: 'projekt_kod', label: 'Kod projektu', source: 'project', description: 'np. P-2026-012' },
  { key: 'projekt_nazwa', label: 'Nazwa projektu', source: 'project', description: 'Z karty projektu' },
  { key: 'adres_budowy', label: 'Adres budowy', source: 'project', description: 'site_address projektu (nie adres klienta)' },
  { key: 'data_rozpoczecia', label: 'Data rozpoczęcia', source: 'project', description: 'start_date projektu, DD.MM.RRRR' },
  { key: 'data_zakonczenia', label: 'Data zakończenia (plan)', source: 'project', description: 'end_date_estimated projektu' },

  // System
  { key: 'data_dzisiejsza', label: 'Data dzisiejsza', source: 'system', description: 'DD.MM.RRRR w momencie generacji' },
  { key: 'firma_nazwa', label: 'Nazwa firmy', source: 'system', description: 'CoreLTB Builders (stała firmowa)' },
  { key: 'firma_email', label: 'E-mail firmy', source: 'system', description: 'Stała firmowa' },

  // Z FORMULARZA danych do umowy (client_contract_data) — auto-podstawiane gdy
  // klient wypelnil formularz (source 'lead', mig 0067). Bez danych: {{klucz}}
  // zostaje dziura, a gate w /generate blokuje umowe bez tych danych.
  { key: 'klient_pesel', label: 'Klient — PESEL', source: 'lead', description: 'Z formularza danych do umowy' },
  { key: 'klient_dowod', label: 'Klient — nr dowodu', source: 'lead', description: 'Seria i numer dowodu — z formularza danych do umowy' },
  { key: 'numer_dzialki', label: 'Numer działki', source: 'lead', description: 'Nr działki i obręb — z formularza danych do umowy' },
  { key: 'pozwolenie_data', label: 'Data pozwolenia na budowę', source: 'lead', description: 'Z formularza (umowa budowlana)' },
  { key: 'projekt_budowlany', label: 'Projekt budowlany (z dnia)', source: 'lead', description: 'Data projektu budowlanego — z formularza (umowa budowlana)' },

  // Druga osoba do umowy — współmałżonek / współinwestor (mig 0072). Puste gdy
  // klient nie zaznaczył jej w formularzu.
  { key: 'wspolinwestor_imie_nazwisko', label: 'Druga osoba — imię i nazwisko', source: 'lead', description: 'Współmałżonek / współinwestor — z formularza danych do umowy' },
  { key: 'wspolinwestor_pesel', label: 'Druga osoba — PESEL', source: 'lead', description: 'Współmałżonek / współinwestor — z formularza' },
  { key: 'wspolinwestor_dowod', label: 'Druga osoba — nr dowodu', source: 'lead', description: 'Seria i numer dowodu drugiej osoby — z formularza' },
  { key: 'wspolinwestor_adres', label: 'Druga osoba — adres', source: 'lead', description: 'Adres drugiej osoby; pusty w formularzu = ten sam co klienta' },

  // Wpisywane RECZNIE przy generacji (source 'input') — nie auto-podstawiane.
  // Wstawialne z panelu zmiennych; przy generacji pojawia sie pole do wpisania.
  { key: 'klient_dowod_organ', label: 'Klient — dowód wydany przez', source: 'input', description: 'Organ wydający dowód (pełnomocnictwo)' },
  { key: 'osoba_odbioru_etapu', label: 'Osoba do odbioru etapu', source: 'input', description: 'Imię i nazwisko — wpisujesz przy generacji' },
  { key: 'gwarancja_okres', label: 'Okres gwarancji', source: 'system', description: 'Stała: 10 lat (Dawid 2026-09-15) — nie wpisywana ręcznie' },
  { key: 'kwota_przestoju', label: 'Kwota za dzień przestoju', source: 'input', description: 'Koszt przestoju za każdy rozpoczęty dzień' },
  { key: 'malzonek_imie_nazwisko', label: 'Współmałżonek — imię i nazwisko', source: 'input', description: 'Drugi podpisujący (umowa małżeństwa)' },
  { key: 'malzonek_dowod', label: 'Współmałżonek — nr dowodu', source: 'input', description: 'Seria i numer dowodu drugiego podpisującego' },
  { key: 'malzonek_pesel', label: 'Współmałżonek — PESEL', source: 'input', description: 'PESEL drugiego podpisującego' },
  { key: 'malzonek_adres', label: 'Współmałżonek — adres', source: 'input', description: 'Adres drugiego podpisującego (jeśli inny)' },
];

/** Klucze wpisywane recznie przy generacji (source 'input'). */
export const TEMPLATE_INPUT_KEYS: ReadonlySet<string> = new Set(
  TEMPLATE_VARIABLES.filter((v) => v.source === 'input').map((v) => v.key),
);

export const TEMPLATE_VARIABLE_KEYS: ReadonlySet<string> = new Set(
  TEMPLATE_VARIABLES.map((v) => v.key),
);

/**
 * Zmienne, ktore MUSI podac KLIENT formularzem (kolumny client_contract_data;
 * nieszyfrowane, nullable — nie wyprowadzalne z leada/oferty/systemu). Sluza do
 * sprawdzania KOMPLETNOSCI per szablon przed generacja (grill B3). Szyfrowane
 * pola (pesel/dowod) sa NOT NULL — obecne gdy ccd istnieje, wiec nie liczone tu.
 */
export const CLIENT_REQUIRED_VARIABLE_KEYS: ReadonlySet<string> = new Set([
  'numer_dzialki',
  'pozwolenie_data',
  'projekt_budowlany',
]);

/**
 * Zwraca klucze zmiennych KLIENTA wymagane przez DANY szablon, ktorych brak
 * (grill B3 — nie generuj umowy z dziurami). `filled` = zbior kluczy zmiennych
 * klienta majacych wartosc w client_contract_data. Pusta lista = komplet.
 */
export function missingClientVariables(
  templateHtml: string,
  filled: ReadonlySet<string>,
): string[] {
  return extractTemplateVariables(templateHtml).filter(
    (k) => CLIENT_REQUIRED_VARIABLE_KEYS.has(k) && !filled.has(k),
  );
}

/**
 * Pole KLIENTA w publicznym formularzu — mapowanie zmiennej szablonu na pole
 * formularza (klucz submit + etykieta + typ + wymagalnosc). Formularz sterowany
 * szablonem (Faza 3) renderuje TYLKO te pola, ktorych dany szablon uzywa.
 */
export interface ClientFormField {
  /** Klucz w contractFormSubmitSchema / kolumnie ccd. */
  key:
    | 'fullName'
    | 'pesel'
    | 'idDocument'
    | 'residentialAddress'
    | 'phone'
    | 'email'
    | 'parcelNumber'
    | 'buildingPermitDate'
    | 'buildingProjectDate'
    | 'buildingSiteAddress';
  /** Zmienna szablonu, ktora to pole wypelnia. */
  variableKey: string;
  label: string;
  type: 'text' | 'date' | 'tel' | 'email';
  required: boolean;
}

export const CLIENT_FORM_FIELDS: readonly ClientFormField[] = [
  { key: 'fullName', variableKey: 'klient_imie_nazwisko', label: 'Imię i nazwisko', type: 'text', required: true },
  { key: 'pesel', variableKey: 'klient_pesel', label: 'PESEL', type: 'text', required: true },
  { key: 'idDocument', variableKey: 'klient_dowod', label: 'Numer i seria dowodu', type: 'text', required: true },
  { key: 'residentialAddress', variableKey: 'klient_adres', label: 'Adres zamieszkania', type: 'text', required: true },
  { key: 'phone', variableKey: 'klient_telefon', label: 'Nr telefonu', type: 'tel', required: false },
  { key: 'email', variableKey: 'klient_email', label: 'E-mail', type: 'email', required: false },
  { key: 'parcelNumber', variableKey: 'numer_dzialki', label: 'Numer działki i obręb', type: 'text', required: false },
  { key: 'buildingPermitDate', variableKey: 'pozwolenie_data', label: 'Data pozwolenia na budowę', type: 'date', required: true },
  { key: 'buildingProjectDate', variableKey: 'projekt_budowlany', label: 'Oznaczenie projektu budowlanego', type: 'text', required: true },
  { key: 'buildingSiteAddress', variableKey: 'adres_budowy', label: 'Adres budowy', type: 'text', required: false },
] as const;

/**
 * Pola klienta, ktorych wymaga DANY szablon (wg jego {{zmiennych}}). Zawsze
 * wlaczamy rdzen tozsamosci (fullName/pesel/idDocument/residentialAddress),
 * bo wystepuje w kazdej umowie — nawet jesli szablon uzywa go posrednio.
 */
export function clientFormFieldsForTemplate(templateHtml: string): ClientFormField[] {
  const used = new Set(extractTemplateVariables(templateHtml));
  const core = new Set(['fullName', 'pesel', 'idDocument', 'residentialAddress']);
  return CLIENT_FORM_FIELDS.filter((f) => used.has(f.variableKey) || core.has(f.key));
}

/** {{zmienna}} — lowercase, cyfry, underscore. Whitespace wewnatrz dozwolony. */
const VARIABLE_RE = /\{\{\s*([a-z0-9_]+)\s*\}\}/g;

/**
 * Wyciaga unikalne klucze zmiennych z HTML szablonu, w kolejnosci
 * pierwszego wystapienia. Uzywane przez edytor (walidacja/podglad)
 * i backend (split known/manual przed generacja).
 */
export function extractTemplateVariables(html: string): string[] {
  const found: string[] = [];
  const seen = new Set<string>();
  for (const match of html.matchAll(VARIABLE_RE)) {
    const key = match[1];
    if (key && !seen.has(key)) {
      seen.add(key);
      found.push(key);
    }
  }
  return found;
}

/**
 * Dzieli zmienne szablonu na znane (auto-podstawiane) i reczne
 * (user wypelnia w formularzu przy generacji).
 */
export function splitTemplateVariables(html: string): {
  known: string[];
  manual: string[];
} {
  const all = extractTemplateVariables(html);
  return {
    // known = auto-podstawiane (w rejestrze i NIE 'input')
    known: all.filter((k) => TEMPLATE_VARIABLE_KEYS.has(k) && !TEMPLATE_INPUT_KEYS.has(k)),
    // manual = spoza rejestru LUB 'input' (wpisywane przy generacji)
    manual: all.filter((k) => !TEMPLATE_VARIABLE_KEYS.has(k) || TEMPLATE_INPUT_KEYS.has(k)),
  };
}

/**
 * Podstawia wartosci w HTML. Zmienne bez wartosci zostaja jako {{klucz}}
 * (widoczne w PDF = sygnal ze czegos brakuje — celowo, zeby nie generowac
 * cicho dokumentu z dziura). Newline w wartosci → <br> (wartosci
 * wielolinijkowe jak {{zakres_prac}} zachowuja lamanie w PDF).
 */
export function fillTemplateVariables(html: string, values: Record<string, string>): string {
  return html.replace(VARIABLE_RE, (whole, key: string) => {
    const value = values[key];
    return value !== undefined && value !== ''
      ? escapeHtml(value).replace(/\n/g, '<br>')
      : whole;
  });
}

/** Escape wartosci wstawianych do HTML (wartosci sa plain text, nie HTML). */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
